import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import ChartHeader from "../ChartHeader";
import ChartEmpty, { CHART_HEIGHT } from "../ChartEmpty";
import { RANGE_COLORS, outOfRangeSeries, phaseSeries, readingChartOptions } from "./phaseReadings";

const RANGE_LEGEND_HEIGHT = 28;

const styles = {
  rangeLegend: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "6px 16px",
    minHeight: `${RANGE_LEGEND_HEIGHT}px`,
    marginTop: "6px",
    fontFamily: "manrope",
    fontSize: "12px",
    color: "#495057",
  },
  keys: { display: "flex", alignItems: "center", flexWrap: "wrap", gap: "6px 16px" },
  key: { display: "inline-flex", alignItems: "center", gap: "6px", whiteSpace: "nowrap" },
  band: {
    width: "16px",
    height: "10px",
    borderRadius: "2px",
    background: "rgba(27, 175, 122, 0.14)",
    borderTop: `1px dashed ${RANGE_COLORS.normal}`,
    borderBottom: `1px dashed ${RANGE_COLORS.normal}`,
  },
  outLine: { width: "16px", height: "3px", borderRadius: "2px", background: RANGE_COLORS.out },
  pill: (ok) => ({
    padding: "3px 10px",
    borderRadius: "999px",
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: ok ? "#127a55" : "#b42318",
    background: ok ? "rgba(27, 175, 122, 0.12)" : "rgba(229, 56, 59, 0.10)",
  }),
};

// "42%" of readings out of range; "<1%" rather than "0%" when any are.
const outOfRangeShare = (count, total) => {
  const percent = (count / total) * 100;
  return percent < 1 ? "<1%" : `${Math.round(percent)}%`;
};

// The key to a chart's safe band, and the share of readings outside it.
function RangeLegend({ range, unit, count, total, multiDay }) {
  const ok = count === 0;
  const what = multiDay ? "days" : "readings";
  return (
    <div style={styles.rangeLegend}>
      <div style={styles.keys}>
        <span style={styles.key}>
          <span style={styles.band} />
          {range.normalLabel} {range.low}–{range.high} {unit}
        </span>
        <span style={styles.key}>
          <span style={styles.outLine} />
          {range.outLabel}
        </span>
      </div>
      <span style={styles.pill(ok)} title={ok ? undefined : `${count} of ${total} ${what} out of range`}>
        {ok ? `All ${range.normalLabel.toLowerCase()}` : `${outOfRangeShare(count, total)} out of range`}
      </span>
    </div>
  );
}

// A device's load-log reading over the selected days: one line per phase for
// a single day, the daily average for several. `reading` is the load log key
// ("v", "p", "f", "pf", "c"); see readingChartOptions for the axis props, and
// phaseSeries for `seriesOptions` (another log list, invalid readings).
// With a `range` (see RANGE_COLORS), the safe band is shaded and readings
// outside it are drawn in red.
export default function PhaseReadingChart({
  selectedDeviceSelectedDayData = [],
  reading,
  name,
  image,
  iconBackgroundColor,
  unit,
  axisTitle,
  decimals,
  min,
  max,
  fromZero,
  seriesOptions,
  range,
}) {
  const days = selectedDeviceSelectedDayData;
  const series = useMemo(
    () => phaseSeries(days, reading, name, seriesOptions),
    [days, reading, name, seriesOptions]
  );
  const { overlays, count, total } = useMemo(
    () => (range ? outOfRangeSeries(series, range) : { overlays: [], count: 0, total: 0 }),
    [series, range]
  );
  const options = useMemo(
    () =>
      readingChartOptions({
        unit,
        axisTitle,
        decimals,
        min,
        max,
        fromZero,
        days,
        range,
        phaseNames: series.map((line) => line.name),
        overlayCount: overlays.length,
      }),
    [unit, axisTitle, decimals, min, max, fromZero, days, range, series, overlays.length]
  );
  const hasData = series.some((line) => line.data.some((point) => point !== null));

  return (
    <div>
      <ChartHeader
        name={name}
        image={image}
        textColor="#322FC8"
        iconBackgroundColor={iconBackgroundColor}
      />
      {hasData ? (
        <>
          {range && <RangeLegend range={range} unit={unit} count={count} total={total} multiDay={days.length > 1} />}
          <div className={range ? "phase-reading-range" : undefined}>
            {range && (
              // Legend entries follow the series, so the ones past the phases
              // are the red overlays: they share their phase's entry.
              <style>{`.phase-reading-range .apexcharts-legend-series:nth-child(n+${series.length + 1}) { display: none !important; }`}</style>
            )}
            <ReactApexChart
              options={options}
              series={[...series, ...overlays]}
              type="line"
              height={range ? CHART_HEIGHT - RANGE_LEGEND_HEIGHT - 6 : CHART_HEIGHT}
            />
          </div>
        </>
      ) : (
        <ChartEmpty kind="line" title={`No ${name.toLowerCase()} readings`} />
      )}
    </div>
  );
}
